import React, { useState } from 'react';
import PageHero from '../components/PageHero';
import { CONTACT_PEOPLE, CONTACT } from '../data/mock';
import { Phone, MapPin, Mail, ArrowRight, CheckCircle2, User } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { useToast } from '../hooks/use-toast';

export default function ContactPage() {
  const [submitted, setSubmitted] = useState(false);
  const { toast } = useToast();
  const [form, setForm] = useState({
    interest: 'RehabVeda R1',
    name: '',
    email: '',
    phone: '',
    org: '',
    message: '',
  });

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast({ title: 'Please fill in required fields', description: 'Name, email and phone are required.' });
      return;
    }
    const prev = JSON.parse(localStorage.getItem('mednosys_leads') || '[]');
    prev.push({ ...form, at: new Date().toISOString() });
    localStorage.setItem('mednosys_leads', JSON.stringify(prev));
    setSubmitted(true);
    toast({ title: 'Enquiry received', description: 'Our team will reach out within 1 business day.' });
  };

  return (
    <div>
      <PageHero
        eyebrow="Contact Us"
        title="Talk to our India team."
        subtitle="Whether you’re a hospital, clinic, patient family, or a Med-Tech founder looking for RFP and SOC2 support — we’re here to help."
      />

      <section className="pb-16 lg:pb-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="mb-14">
            <span className="text-[12.5px] uppercase tracking-[0.22em] font-semibold text-[#E85A18]">Our leadership</span>
            <h2 className="mt-3 text-[30px] lg:text-[38px] leading-[1.1] tracking-tight text-[#0F1B2D] font-semibold" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Reach the right team member directly.
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {CONTACT_PEOPLE.map((p) => (
              <div key={p.name} className="group rounded-2xl border border-orange-100 bg-white p-6 hover:shadow-[0_25px_60px_-30px_rgba(232,90,24,0.35)] transition-shadow duration-500">
                <div className="flex items-center gap-4">
                  <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-[#FFB27A] to-[#E85A18] flex items-center justify-center text-white font-semibold text-[18px]" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                    {p.initials}
                  </div>
                  <div>
                    <div className="text-[17px] font-semibold text-[#0F1B2D]" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                      {p.name}
                    </div>
                    <div className="text-[12.5px] text-[#E85A18] font-medium">{p.role}</div>
                  </div>
                </div>
                <div className="mt-5 space-y-2.5 text-[13.5px] text-slate-600">
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4 text-[#FF7A2E]" />
                    <span>{p.territory}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Phone className="h-4 w-4 text-[#FF7A2E]" />
                    <span className="font-medium text-[#0F1B2D]">{p.phone}</span>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-orange-100 text-[12.5px] text-slate-500">
                  {p.domain}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-24 bg-gradient-to-b from-[#FDF8F2] to-[#FFF6EE]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-14">
          <div className="lg:col-span-5">
            <span className="text-[12.5px] uppercase tracking-[0.22em] font-semibold text-[#E85A18]">Send us a message</span>
            <h2 className="mt-3 text-[32px] lg:text-[44px] leading-[1.05] tracking-tight text-[#0F1B2D] font-semibold" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Let’s work together.
            </h2>
            <p className="mt-5 text-[16px] leading-[1.7] text-slate-600 max-w-md">
              Fill in the form and we’ll route you to the right specialist — distribution, deployment or Med-Tech advisory. Usual response time: one business day.
            </p>
            <div className="mt-10 space-y-5">
              <InfoRow icon={MapPin} label="Head Office" value={CONTACT.addressIN} />
              <InfoRow icon={Phone} label="Sales" value={CONTACT.phones.join('  ·  ')} />
              <InfoRow icon={Mail} label="Email" value={<a href={`mailto:${CONTACT.email}`} className="hover:text-[#E85A18]">{CONTACT.email}</a>} />
            </div>
          </div>

          <div className="lg:col-span-7">
            <form onSubmit={submit} className="rounded-3xl bg-white border border-orange-100 shadow-[0_30px_80px_-30px_rgba(232,90,24,0.35)] p-7 lg:p-10">
              {submitted ? (
                <div className="py-12 text-center">
                  <div className="inline-flex h-14 w-14 rounded-full bg-gradient-to-br from-[#FFB27A] to-[#E85A18] items-center justify-center">
                    <CheckCircle2 className="h-7 w-7 text-white" />
                  </div>
                  <h3 className="mt-6 text-[26px] font-semibold text-[#0F1B2D]" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                    Thank you, {form.name.split(' ')[0] || 'friend'}!
                  </h3>
                  <p className="mt-2 text-slate-600 max-w-md mx-auto">
                    Your enquiry for <b>{form.interest}</b> has been recorded. Our team will reach out shortly.
                  </p>
                  <button type="button" onClick={() => { setSubmitted(false); setForm({ interest: 'RehabVeda R1', name: '', email: '', phone: '', org: '', message: '' }); }} className="mt-8 text-[14px] font-medium text-[#E85A18] hover:text-[#B54A15]">
                    Submit another enquiry
                  </button>
                </div>
              ) : (
                <>
                  <div className="mb-6">
                    <Label className="text-[13px] font-semibold text-[#0F1B2D]">I’m interested in *</Label>
                    <RadioGroup value={form.interest} onValueChange={(v) => update('interest', v)} className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2">
                      {['RehabVeda R1', 'Stroke Insightz', 'SOC2 / IT', 'RFP & Sales'].map((o) => (
                        <label key={o} htmlFor={`opt-${o}`} className={`cursor-pointer rounded-xl border px-3 py-2.5 text-[12.5px] font-medium transition-all flex items-center gap-2 ${form.interest === o ? 'border-[#FF7A2E] bg-[#FFF6EE] text-[#B54A15]' : 'border-slate-200 text-slate-700 hover:border-orange-200'}`}>
                          <RadioGroupItem id={`opt-${o}`} value={o} className="text-[#E85A18]" />
                          {o}
                        </label>
                      ))}
                    </RadioGroup>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <FieldInput id="name" label="Full name *" value={form.name} onChange={(v) => update('name', v)} placeholder="Dr. Priya Sharma" />
                    <FieldInput id="email" label="Email *" type="email" value={form.email} onChange={(v) => update('email', v)} placeholder="you@hospital.in" />
                    <FieldInput id="phone" label="Phone *" value={form.phone} onChange={(v) => update('phone', v)} placeholder="+91 …" />
                    <FieldInput id="org" label="Hospital / Company" value={form.org} onChange={(v) => update('org', v)} placeholder="Your organisation" />
                  </div>
                  <div className="mt-4">
                    <Label htmlFor="msg" className="text-[13px] font-semibold text-[#0F1B2D]">Message</Label>
                    <Textarea id="msg" value={form.message} onChange={(e) => update('message', e.target.value)} placeholder="Tell us about your requirement, city, or timeline…" className="mt-2 min-h-[110px] rounded-xl border-slate-200 focus-visible:ring-[#FF7A2E] focus-visible:border-[#FF7A2E]" />
                  </div>
                  <Button type="submit" className="mt-6 group h-12 px-6 rounded-full bg-[#0F1B2D] hover:bg-[#1a2942] text-white text-[14.5px] font-medium">
                    Send enquiry <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                  </Button>
                </>
              )}
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}

function FieldInput({ id, label, value, onChange, placeholder, type = 'text' }) {
  return (
    <div>
      <Label htmlFor={id} className="text-[13px] font-semibold text-[#0F1B2D]">{label}</Label>
      <Input id={id} type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="mt-2 h-11 rounded-xl border-slate-200 focus-visible:ring-[#FF7A2E] focus-visible:border-[#FF7A2E]" />
    </div>
  );
}

function InfoRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-4">
      <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-[#FFECD9] to-[#FFD5B4] flex items-center justify-center">
        <Icon className="h-5 w-5 text-[#E85A18]" />
      </div>
      <div>
        <div className="text-[13px] uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
        <div className="text-[15px] text-[#0F1B2D] mt-1">{value}</div>
      </div>
    </div>
  );
}
